import React, { useState, useEffect } from "react";
import axios from "axios";
import { styled, Box, Typography } from "@mui/material";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useLazyQuery } from "@apollo/client";
import { GET_GA_DASHBOARD } from "../../gql/queries/ga4";

const COLORS = ["#fbbc04", "#ea4335", "#34a853", "#4285f4", "#9aa0a6"];

const GADashboardWrapper = styled("section")(({ theme }) => ({
  marginTop: "20px",

  "& .dashboardHeader": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    padding: "20px",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,

    "@media (max-width:768px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "15px",
    },
  },

  "& h2": {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  "& .dateRange": {
    display: "flex",
    alignItems: "center",
    gap: "10px",

    "& label": {
      fontSize: "14px",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },

    "& select": {
      padding: "8px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontSize: "14px",
      cursor: "pointer",
    },
  },

  "& .metricsGrid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "20px",
    marginBottom: "20px",
  },

  "& .metricCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    padding: "20px",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    borderTop: `3px solid ${theme.palette.primary.main}`,
    textAlign: "center",
  },

  "& .metricLabel": {
    fontSize: "13px",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: "8px",
  },

  "& .metricValue": {
    fontSize: "26px",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  "& .dashboardCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    padding: "24px",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    // marginBottom: "20px",
    marginTop: "15px",
  },

  "& .dashboardCard h3": {
    fontSize: "18px",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: "16px",
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingBottom: "8px",
  },

  "& .dashboardGrid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
    gap: "20px",
    // marginBottom: "20px",

    "@media (max-width:768px)": {
      gridTemplateColumns: "1fr",
    },
  },

  "& .tableContainer": {
    overflowX: "auto",
  },

  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },

  "& thead": {
    backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f8f9fa",
  },

  "& th": {
    padding: "12px",
    textAlign: "left",
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },

  "& td": {
    padding: "10px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },

  "& tbody tr:hover": {
    backgroundColor: theme.palette.action.hover,
  },

  "& .pageUrl": {
    maxWidth: "300px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "monospace",
    fontSize: "13px",
  },

  "& .sourceText": {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  "& .loading": {
    textAlign: "center",
    padding: "40px",
    fontSize: "15px",
    color: theme.palette.text.secondary,
  },

  "& .error": {
    backgroundColor: theme.palette.mode === "dark" ? "#4a1f24" : "#f8d7da",
    color: theme.palette.mode === "dark" ? "#ffb3b3" : "#721c24",
    padding: "14px",
    borderRadius: "6px",
    textAlign: "center",
  },
}));

function GADashboard({ email, propertyId }) {
  const [loading, setLoading] = useState(true);
  const [dataa, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("30"); // days
  const [fetchGaDashboard] = useLazyQuery(GET_GA_DASHBOARD, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (propertyId) {
      fetchDashboardData();
    }
  }, [propertyId, dateRange]);

  // const fetchDashboardData = async () => {
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     const endDate = new Date().toISOString().split("T")[0];
  //     const startDate = new Date(Date.now() - parseInt(dateRange, 10) * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  //     const params = new URLSearchParams({
  //       property_id: propertyId,
  //       start_date: startDate,
  //       end_date: endDate,
  //     });

  //     if (email) {
  //       params.append("email", email);
  //     }

  //     const response = await axios.get(`http://localhost:8000/api/ga/analytics/dashboard?${params.toString()}`);

  //     if (response.data.success) {
  //       setData(response.data.data);
  //     } else {
  //       setError("Failed to fetch analytics data");
  //     }
  //   } catch (err) {
  //     setError(err.response?.data?.detail || err.message || "Failed to fetch analytics data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const endDate = new Date().toISOString().split("T")[0];
      const startDate = new Date(Date.now() - parseInt(dateRange, 10) * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

      const { data } = await fetchGaDashboard({
        variables: {
          property_id: propertyId,
          start_date: startDate,
          end_date: endDate,
          email: email || null,
        },
      });

      const response = data?.gaDashboard;

      if (response?.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch analytics data");
      }
    } catch (err) {
      console.error("Error fetching GA dashboard:", err);

      setError(err?.graphQLErrors?.[0]?.message || err?.message || "Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      // <Box className="ga-dashboard">
      <GADashboardWrapper>
        <Box className="dashboardCard">
          <Box className="loading">Loading Google Analytics data...</Box>
        </Box>
      </GADashboardWrapper>
      // </Box>
    );
  }

  if (error) {
    return (
      // <Box className="ga-dashboard">
      <GADashboardWrapper>
        <Box className="dashboardCard">
          <Box className="error">{error}</Box>
        </Box>
      </GADashboardWrapper>
      // </Box>
    );
  }

  if (!dataa) {
    return null;
  }

  return (
    // <Box className="ga-dashboard">
    <GADashboardWrapper>
      <Box className="dashboardHeader">
        <Typography variant="h2">Google Analytics Dashboard</Typography>
        <Box className="dateRange">
          <Typography component="label" htmlFor="ga-date-range">
            Date Range:{" "}
          </Typography>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Box className="metricsGrid">
        <Box className="metricCard">
          <Box className="metricLabel">Active Users</Box>
          <Box className="metricValue">{dataa.overview?.activeUsers?.toLocaleString() || 0}</Box>
        </Box>
        <Box className="metricCard">
          <Box className="metricLabel">Sessions</Box>
          <Box className="metricValue">{dataa.overview?.sessions?.toLocaleString() || 0}</Box>
        </Box>
        <Box className="metricCard">
          <Box className="metricLabel">Page Views</Box>
          <Box className="metricValue">{dataa.overview?.pageViews?.toLocaleString() || 0}</Box>
        </Box>
        <Box className="metricCard">
          <Box className="metricLabel">Bounce Rate</Box>
          <Box className="metricValue">{dataa.overview?.bounceRate || 0}%</Box>
        </Box>
        <Box className="metricCard">
          <Box className="metricLabel">Avg Session Duration</Box>
          <Box className="metricValue">{formatDuration(dataa.overview?.avgSessionDuration || 0)}</Box>
        </Box>
        <Box className="metricCard">
          <Box className="metricLabel">Conversions</Box>
          <Box className="metricValue">{dataa.overview?.conversions?.toLocaleString() || 0}</Box>
        </Box>
      </Box>

      {/* Trends Chart */}
      <Box className="dashboardCard">
        <Typography variant="h3">Performance Trends</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dataa.trends || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis yAxisId="left" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="users" stroke="#fbbc04" name="Users" strokeWidth={2} />
            <Line yAxisId="left" type="monotone" dataKey="sessions" stroke="#34a853" name="Sessions" strokeWidth={2} />
            <Line yAxisId="left" type="monotone" dataKey="pageViews" stroke="#4285f4" name="Page Views" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box className="dashboardGrid">
        {/* Top Pages */}
        <Box className="dashboardCard">
          <Typography variant="h3">Top Pages</Typography>
          <Box className="tableContainer">
            <table className="table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Page Views</th>
                  <th>Users</th>
                  <th>Avg Duration</th>
                  <th>Bounce Rate</th>
                </tr>
              </thead>
              <tbody>
                {(dataa.topPages || []).slice(0, 10).map((page, index) => (
                  <tr>
                    <td className="pageUrl">{page.page}</td>
                    <td>{page.pageViews.toLocaleString()}</td>
                    <td>{page.users.toLocaleString()}</td>
                    <td>{formatDuration(page.avgSessionDuration)}</td>
                    <td>{page.bounceRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Traffic Sources */}
        <Box className="dashboardCard">
          <Typography variant="h3">Traffic Sources</Typography>
          <Box className="tableContainer">
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Sessions</th>
                  <th>Users</th>
                  <th>Bounce Rate</th>
                </tr>
              </thead>
              <tbody>
                {(dataa.trafficSources || []).slice(0, 10).map((source, index) => (
                  <tr>
                    <td className="sourceText">{source.source}</td>
                    <td>{source.sessions.toLocaleString()}</td>
                    <td>{source.users.toLocaleString()}</td>
                    <td>{source.bounceRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>

      <Box className="dashboardGrid">
        {/* Device Breakdown */}
        <Box className="dashboardCard">
          <Typography variant="h3">Device Breakdown</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={dataa.devices || []} dataKey="sessions" nameKey="device" cx="50%" cy="50%" outerRadius={80} label>
                {(dataa.devices || []).map((entry, index) => (
                  <Cell fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Country Breakdown */}
        <Box className="dashboardCard">
          <Typography variant="h3">Top Countries</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dataa.countries?.slice(0, 10) || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="sessions" fill="#fbbc04" name="Sessions" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </GADashboardWrapper>
    // </Box>
  );
}

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

export default GADashboard;
